import i18n from '@/utils/i18n';
import React from 'react';
import { BORDER_RADIUS_SMALL, TYPOGRAPHY } from '@/styles';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { formatDate } from '@/utils/date';
import { MultiStyleText } from '@/components/multi-style-text';
import { OrderableItemListItemImage } from '@/components/orders/orderable-item-list-item-image';
import { SampleRequestVariantPackFieldsFragment } from '@/types';
import { StyleProp, Text, View, ViewStyle } from 'react-native';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useItemDisplayOptions } from '@/contexts/new-order-context';
import { useSmallLayoutSizeValue } from '@/contexts/layout-context';

interface Props {
  onNotReceivedPress: () => void;
  onReceivedPress: () => void;
  photoURLs: string[];
  sampleRequest?: SampleRequestVariantPackFieldsFragment;
}

export const SampleRequestFollowUpInitial: React.FC<Props> = ({
  onNotReceivedPress,
  onReceivedPress,
  photoURLs,
  sampleRequest,
}) => {
  const [styles] = useStyles();
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const { hidePlaceholderImages } = useItemDisplayOptions();

  const buttonsWrapperStyle = useSmallLayoutSizeValue<StyleProp<ViewStyle>>(
    null,
    styles.buttonsHorizontal,
  );

  const buttonsStyle = useSmallLayoutSizeValue<StyleProp<ViewStyle>>(
    null,
    styles.flexOne,
  );

  if (!sampleRequest) {
    return null;
  }
  const { advertiser_name: advertiserName, variant_pack: variantPack } =
    sampleRequest;
  const {
    uuid: sampleRequestUUID,
    requested_at: requestedAt,
    requested_by: requestedBy,
  } = sampleRequest?.sample_request || {};

  const trackingProperties = {
    restaurantUUID,
    sampleRequestUUID,
    supplierUUID,
  };

  const altText = [
    variantPack?.item.display_name,
    variantPack?.external_item_id,
  ]
    .filter(Boolean)
    .join('-');

  return (
    <>
      <View style={styles.requestedSample}>
        <OrderableItemListItemImage
          alt={altText}
          hidePlaceholderImages={hidePlaceholderImages}
          imageSize={56}
          imageURLs={photoURLs}
          style={styles.image}
        />
        <View style={styles.flexOne}>
          <Text
            style={[TYPOGRAPHY.pBold, styles.smallText, styles.advertiserName]}
          >
            {advertiserName}
          </Text>
          <Text numberOfLines={1} style={[TYPOGRAPHY.pBold, styles.smallText]}>
            {variantPack?.item.display_name}
          </Text>
          <MultiStyleText
            defaultStyle={[TYPOGRAPHY.p, styles.smallText]}
            emphasisStyle={[TYPOGRAPHY.p, styles.smallText]}
            forceSingleLine
          >
            {i18n.t('sample_requested.message', {
              date: formatDate(requestedAt),
              requestor:
                requestedBy?.supplier_employee_overrides[0]?.name ??
                requestedBy?.name ??
                '',
            })}
          </MultiStyleText>
        </View>
      </View>
      <View style={[buttonsWrapperStyle, styles.buttonsWithGap]}>
        <Button
          containerStyle={buttonsStyle}
          onPress={onReceivedPress}
          testID="follow_up_button_yes"
          trackingData={{
            name: 'sample_request_follow_up.sample_received',
            properties: trackingProperties,
          }}
          type="secondary"
        >
          {i18n.t('yes')}
        </Button>
        <Button
          containerStyle={buttonsStyle}
          onPress={onNotReceivedPress}
          testID="follow_up_button_no"
          trackingData={{
            name: 'sample_request_follow_up.sample_not_received',
            properties: trackingProperties,
          }}
          type="secondary"
        >
          {i18n.t('no')}
        </Button>
      </View>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  advertiserName: {
    color: theme.colors.inactiveText,
    textTransform: 'uppercase',
  },
  buttonsHorizontal: {
    flexDirection: 'row',
  },
  buttonsWithGap: {
    gap: 16,
  },
  flexOne: {
    flex: 1,
  },
  image: {
    borderRadius: BORDER_RADIUS_SMALL,
  },
  requestedSample: {
    alignItems: 'center',
    backgroundColor: theme.colors.background,
    borderRadius: BORDER_RADIUS_SMALL,
    flexDirection: 'row',
    gap: 16,
    marginBottom: 24,
    paddingHorizontal: 12,
    paddingVertical: 16,
  },
  smallText: {
    fontSize: theme.fontSizes.small,
  },
}));
